// Invoice-related filters

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createRadioFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const invoiceFilters: FilterRegistry = {
	invoiceKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by invoice ID, external ID, client, or PO...',
			objectTable: 'invoice',
			listKeys: ['invoice', 'invoice-client-embedded'],
			// PD-14788: 'order.customerPO' is not an invoice column — the backend
			// pre-resolves it to the matching invoice ids up front (see
			// DERIVED_KEYWORD_ID_QUERIES in typeormFilterBuilder.ts). It was
			// originally kept out of this chain because matching it inline
			// roughly doubled every keyword search; the standalone resolution
			// against the covering idx_order_deleted_customer_po costs ~0.1s,
			// so the one search box users reach for finds POs too.
			fulltextFields: [
				'externalId',
				'externalId2',
				'_id',
				'c.name',
				'c.externalId',
				'c.externalId2',
				'order.customerPO',
			],
		}),
		queryParamName: 'invoiceKeywords',
	},

	// PD-14788: dedicated customer-PO search. Superseded as a default by PO
	// matching in the general search above, so no list ships it in
	// `activeFilters` any more — it stays registered so existing saved lists
	// that captured it keep working and it can be re-added through the
	// Configure List Filters tab.
	invoiceCustomerPO: {
		...createKeywordFilter({
			label: 'Customer PO',
			placeholder: 'Search by customer PO...',
			objectTable: 'invoice',
			listKeys: ['invoice', 'invoice-client-embedded'],
			fulltextFields: ['order.customerPO'],
		}),
		queryParamName: 'invoiceCustomerPO',
	},

	invoiceCompany: {
		...createEntityLookupFilter({
			label: 'Company',
			placeholder: 'Search companies...',
			apiEndpoint: '/users/companies',
			objectTable: 'invoice',
			fieldName: 'fk_company__id',
			listKeys: ['invoice', 'invoice-client-embedded'],
			displayFields: {
				id: 'id',
				primary: 'label',
			},
			colorScheme: 'purple',
		}),
		queryParamName: 'invoiceCompany',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	invoiceDateRange: {
		...createDateRangeFilter({
			label: 'Invoice Date',
			objectTable: 'invoice',
			dateField: 'date',
			listKeys: ['invoice', 'invoice-client-embedded'],
		}),
		queryParamName: 'invoiceDateRange',
	},

	invoiceDueDateRange: {
		...createDateRangeFilter({
			label: 'Due Date',
			objectTable: 'invoice',
			dateField: 'dueDate',
			listKeys: ['invoice', 'invoice-client-embedded'],
		}),
		queryParamName: 'invoiceDueDateRange',
		queryMapping: {
			objectTable: 'invoice',
			dateField: 'dueDate',
			joinType: 'direct',
			// When filtering "dueDate >= today" (Current tab), also include invoices
			// with no due date — they have no missed deadline so they're not past due
			includeNullDates: true,
		},
	},

	invoiceSalesRep: {
		type: 'multi-select',
		label: 'Sales Rep',
		placeholder: 'Select sales representatives...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/invoices/salesrep/options',
		// PD-16348: "Unassigned" (the `'null'` token) is the opt-in a user who
		// may see unassigned accounts can switch on; the list rule bounds who is
		// offered it.
		options: [
			{ id: 'ME', label: 'Me' },
			{ id: 'null', label: 'Unassigned' },
		],
		queryParamName: 'invoiceSalesRep',
		multiple: true,
		queryMapping: {
			objectTable: 'invoice',
			joinType: 'custom',
		},
		format: 'array',
		// Ids go as strings (PD-16348): a numeric transform turned the
		// `'null'` token into NaN, which was then dropped.
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['invoice', 'invoice-client-embedded'],
	},

	invoicePaymentStatus: {
		...createRadioFilter({
			label: 'Payment Status',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'outstanding', label: 'Outstanding' },
				{ id: 'paid_in_full', label: 'Paid in Full' },
			],
			defaultValue: { id: 'all', label: 'All' },
			objectTable: 'invoice',
			fieldName: 'paymentStatus',
			listKeys: ['invoice', 'invoice-client-embedded'],
		}),
		queryParamName: 'invoicePaymentStatus',
		queryMapping: {
			objectTable: 'invoice',
			joinType: 'custom',
		},
	},

	// PD-15050 — sent/unsent facet against the document send-history ledger.
	// "Unsent" pulls up invoices no customer has ever received — the recovery
	// tool for everything created before send history shipped and anything
	// that slips through later.
	invoiceEmailSent: {
		...createRadioFilter({
			label: 'Emailed to Customer',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'sent', label: 'Sent' },
				{ id: 'unsent', label: 'Unsent' },
			],
			defaultValue: { id: 'all', label: 'All' },
			objectTable: 'invoice',
			fieldName: 'emailSent',
			listKeys: ['invoice', 'invoice-client-embedded'],
		}),
		queryParamName: 'invoiceEmailSent',
		queryMapping: {
			objectTable: 'invoice',
			joinType: 'custom',
		},
	},
};
